import styled, { keyframes, css } from 'styled-components';

import { ProgressBarColor, ProgressBarSize } from './types';

export const RootStyled = styled.div<{ percentage?: number; fullWidth?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 8px;
`;

const progressSlideAnimation = (percentage: number, animationStartPercentage = 0) => keyframes`
    0% {
      width: ${`${animationStartPercentage}%`};
    }
    100% {
      width: ${`${percentage}%;`};
    }
`;

const sizeStyles: Record<ProgressBarSize, { height: number; radius: number }> = {
  xs: {
    height: 4,
    radius: 2,
  },
  sm: {
    height: 8,
    radius: 5,
  },
  lg: {
    height: 24,
    radius: 12,
  },
};

const colorStyles: Record<ProgressBarColor, { bar: ReturnType<typeof css>; track: ReturnType<typeof css> }> = {
  primary: {
    track: css`
      background: ${(p) => p.theme.colors.neutral.container};
    `,
    bar: css`
      background: ${(p) => p.theme.colors.primary.outline};
    `,
  },
  gradient: {
    track: css`
      background: ${(p) => p.theme.colors.neutral.container};
    `,
    bar: css`
      background: linear-gradient(
        90deg,
        ${(p) => p.theme.colors.tertiary.outline} 1.86%,
        ${(p) => p.theme.colors.primary.outline} 101%
      );
      box-shadow:
        0 -2px 4px 0 rgba(0, 0, 0, 0.25) inset,
        0 3px 4px 0 rgba(255, 255, 255, 0.5) inset;
    `,
  },
};

export const ProgressBarStyled = styled.div<{
  percentage: number;
  animated?: boolean;
  roundedBar: boolean;
  roundedTrack: boolean;
  animationStartPercentage?: number;
  color?: ProgressBarColor;
  size?: ProgressBarSize;
}>`
  width: 100%;
  height: ${({ size }) => sizeStyles[size || 'sm'].height}px;
  ${({ color }) => colorStyles[color || 'primary'].track}
  border-radius: ${({ roundedTrack, size }) => (roundedTrack ? `${sizeStyles[size || 'sm'].radius}px` : '0px')};
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    width: ${({ percentage }) => `${percentage}%;`};
    ${({ animated, percentage, animationStartPercentage }) =>
      animated &&
      css`
        animation: 1s ease-out 0s 1 ${progressSlideAnimation(percentage, animationStartPercentage)};
      `}
    height: ${({ size }) => sizeStyles[size || 'sm'].height}px;
    ${({ color }) => colorStyles[color || 'primary'].bar}
    border-radius: ${({ roundedBar, size }) => (roundedBar ? `${sizeStyles[size || 'sm'].radius}px` : '0px')};
    display: block;
  }
`;

export const LabelStyled = styled.span`
  ${(p) => p.theme.typography.caption};
  color: ${(p) => p.theme.colors.neutral.onContainer};
`;
